/**
 * Copyright © 2026 Delicious AI, LLC
 */

import React from 'react'
import type { DetectionImageSession } from '@/graphql'

import { toDetectionBox } from './detectionUtils'
import { useDetectionEditor } from './useDetectionEditor'
import { useDetectionSession } from './useDetectionSession'

type DetectionWorkflowOptions = {
  onApproveSuccess?: () => Promise<unknown> | unknown
}

export function useDetectionWorkflow(
  initialSession: DetectionImageSession | null = null,
  { onApproveSuccess }: DetectionWorkflowOptions = {},
) {
  const session = useDetectionSession(initialSession)
  const editor = useDetectionEditor({
    image: session.image
      ? { id: session.image.id, imageUrl: session.image.imageUrl }
      : null,
    canEdit: session.canEdit,
    onApproveSuccess: onApproveSuccess ?? session.requestNextImage,
    markImageHandled: session.markImageHandled,
    unmarkImageHandled: session.unmarkImageHandled,
  })
  const { handleIncomingImage, mergeDiscoveredTags, setSelectedBoxIds } = editor
  const syncedImageIdRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    const nextImageId = session.image?.id ?? null
    if (syncedImageIdRef.current === nextImageId) {
      return
    }

    const nextBoxes = session.image
      ? session.image.boundingBoxes.map(toDetectionBox)
      : []
    syncedImageIdRef.current = nextImageId
    handleIncomingImage(nextBoxes)
    if (session.image) {
      mergeDiscoveredTags(nextBoxes)
    }
  }, [handleIncomingImage, mergeDiscoveredTags, session.image])

  const handleNextImage = async () => {
    setSelectedBoxIds([])
    await session.handleNextImage(session.image?.id ?? null, session.canEdit)
  }

  const handleFlaggedImage = React.useCallback(async () => {
    if (!session.image) return
    const currentImageId = session.image.id
    setSelectedBoxIds([])
    await session.requestNextImage()
    session.markImageHandled(currentImageId)
  }, [
    session.image,
    session.markImageHandled,
    session.requestNextImage,
    setSelectedBoxIds,
  ])

  return {
    ...editor,
    canEdit: session.canEdit,
    error: session.error,
    handleFlaggedImage,
    handleNextImage,
    handleReload: session.handleReload,
    image: session.image,
    leaseLock: session.leaseLock,
    loading: session.loading,
    loadingImage: session.loadingImage,
    loadingNext: session.loadingNext,
    noItems: session.noItems,
    returnedFeedbackCount: session.returnedFeedbackCount,
    reviewFeedback: session.reviewFeedback,
    statusMessage: session.statusMessage,
  }
}
